import React, { useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../../components/Navbar';
import loader from '../../images/load.gif';
import styles from '../../styles/createproduct.module.css';

function CreateProduct() {
  const [name, setName] = useState('');
  const [image, setImage] = useState('');
  const [brand, setBrand] = useState('');
  const [countInStock, setCountInStock] = useState('');
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const validate = () => {
    if (!name || !name.trim()) {
      toast.error('Please provide product name', {
        position: 'bottom-right',
        theme: 'colored'
      });
      return false;
    }
    if (!image || !image.trim()) {
      toast.error('Please provide image URL', {
        position: 'bottom-right',
        theme: 'colored'
      });
      return false;
    }
    if (!price || parseFloat(price) <= 0) {
      toast.error('Please provide a valid price', {
        position: 'bottom-right',
        theme: 'colored'
      });
      return false;
    }
    if (!brand || !brand.trim()) {
      toast.error('Please provide brand name', {
        position: 'bottom-right',
        theme: 'colored'
      });
      return false;
    }
    if (!countInStock || parseInt(countInStock) < 0) {
      toast.error('Please provide valid stock quantity', {
        position: 'bottom-right',
        theme: 'colored'
      });
      return false;
    }
    if (!description || !description.trim()) {
      toast.error('Please provide product description', {
        position: 'bottom-right',
        theme: 'colored'
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = validate();
    
    if (isValid) {
      try {
        setLoading(true);
        const res = await axios.post('https://pro-shop-backend.vercel.app/api/products', {
          name: name.trim(),
          image: image.trim(),
          brand: brand.trim(),
          countInStock: parseInt(countInStock),
          price: parseFloat(price),
          description: description.trim()
        });
        setLoading(false);

        if (res.data.success === true) {
          toast.success(res.data.message || 'Product created successfully!', {
            position: 'bottom-right',
            theme: 'colored'
          });
          navigate('/admin/allproducts');
        } else {
          toast.error(res.data.message || 'Failed to create product', {
            position: 'bottom-right',
            theme: 'colored'
          });
        }
      } catch (error) {
        setLoading(false);
        const errorMessage = error.response?.data?.message || 'Something went wrong. Please try again.';
        toast.error(errorMessage, {
          position: 'bottom-right',
          theme: 'colored'
        });
      }
    }
  };

  return (
    <div className={styles.createProductContainer}>
      <Navbar />
      
      <div className={styles.createProductContent}>
        <div className={styles.createProductCard}>
          <div className={styles.headerSection}>
            <h3 className={styles.pageTitle}>Create New Product</h3>
            <p className={styles.pageSubtitle}>Fill in the details below to add a new product</p>
          </div>

          <form className={styles.createProductForm} onSubmit={handleSubmit}>
            <div className={styles.formGrid}>
              <div className={styles.formColumn}>
                <div className={styles.formGroup}>
                  <label htmlFor="productName" className={styles.formLabel}>
                    Product Name <span className={styles.required}>*</span>
                  </label>
                  <input
                    id="productName"
                    className={styles.formInput}
                    value={name}
                    autoComplete="off"
                    onChange={(e) => setName(e.target.value)}
                    type="text"
                    placeholder="Enter product name"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="productPrice" className={styles.formLabel}>
                    Price ($) <span className={styles.required}>*</span>
                  </label>
                  <input
                    id="productPrice"
                    className={styles.formInput}
                    value={price}
                    autoComplete="off"
                    onChange={(e) => setPrice(e.target.value)}
                    type="number"
                    placeholder="Enter price in USD"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="productImage" className={styles.formLabel}>
                    Image URL <span className={styles.required}>*</span>
                  </label>
                  <input
                    id="productImage"
                    className={styles.formInput}
                    value={image}
                    onChange={(e) => setImage(e.target.value)}
                    type="text"
                    autoComplete="off"
                    placeholder="Enter image URL"
                  />
                  {image && (
                    <div className={styles.imagePreview}>
                      <img src={image} alt="Product preview" />
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.formColumn}>
                <div className={styles.formGroup}>
                  <label htmlFor="productBrand" className={styles.formLabel}>
                    Brand <span className={styles.required}>*</span>
                  </label>
                  <input
                    id="productBrand"
                    className={styles.formInput}
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    type="text"
                    autoComplete="off"
                    placeholder="Enter brand name"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="productStock" className={styles.formLabel}>
                    Stock Quantity <span className={styles.required}>*</span>
                  </label>
                  <input
                    id="productStock"
                    className={styles.formInput}
                    value={countInStock}
                    onChange={(e) => setCountInStock(e.target.value)}
                    type="number"
                    autoComplete="off"
                    placeholder="Enter stock quantity"
                    min="0"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="productDescription" className={styles.formLabel}>
                    Description <span className={styles.required}>*</span>
                  </label>
                  <textarea
                    id="productDescription"
                    className={styles.formTextarea}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter product description"
                    rows="6"
                  />
                </div>
              </div>
            </div>

            <div className={styles.formActions}>
              {loading ? (
                <button className={styles.submitBtn} disabled>
                  <img className={styles.loaderImg} src={loader} alt="loader" />
                  <span>Creating Product...</span>
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className={styles.cancelBtn}
                    onClick={() => navigate('/admin/allproducts')}
                  >
                    Cancel
                  </button>
                  <button type="submit" className={styles.submitBtn}>
                    Create Product
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>

      <ToastContainer
        position="bottom-right"
        theme="colored"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}

export default CreateProduct;